import { useNavigate } from "react-router-dom";
import { Icon, Typography, Button } from "@/components/atoms";
import { useLayout, useOperator, useSubscriptionAssignment } from "@/hooks";
import { clsx } from "@/utils";
import { formatCurrency } from "@/utils/formatCurrency";

interface TrialSubscriptionBannerProps extends React.HTMLAttributes<HTMLDivElement> {}

export const TrialSubscriptionBanner = ({ className, ...props }: TrialSubscriptionBannerProps) => {
  const navigate = useNavigate();
  const { roles } = useOperator();
  const {
    layout: { sidebarOpen },
  } = useLayout();

  // Get subscription data directly from the hook
  const { subscriptionStatus, isLoading } = useSubscriptionAssignment();

  // Don't show anything while loading
  if (isLoading) return null;

  // Only show for Owner and Fa_Access roles
  const isOwnerOrFa = roles.includes("Owner") || roles.includes("Fa_Access");
  if (!isOwnerOrFa) return null;

  // Only show if we're in trial period
  if (!subscriptionStatus.isInTrial) return null;

  // Derive banner props from subscription status
  const daysLeft = subscriptionStatus.trialDaysLeft ?? 0;
  const planName = subscriptionStatus.baseSubscription?.subscription.name ?? "Enterprise";
  const userType = subscriptionStatus.userType;
  const planPrice = subscriptionStatus.baseSubscription
    ? formatCurrency(subscriptionStatus.baseSubscription.subscription.subscriptionFee, 0, "AUD") + "/month"
    : "$999/month";

  // Format billing start date from trialEndsAt
  const getBillingStartDate = () => {
    if (!subscriptionStatus.trialEndsAt) return "soon";
    const date = new Date(subscriptionStatus.trialEndsAt);
    return date.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  };

  const billingStartDate = getBillingStartDate();
  const isUrgent = daysLeft <= 2;

  // Show urgent styling for all users when trial is ending soon (≤2 days)
  const showUrgentStyling = isUrgent;

  const handleStartSubscription = () => {
    if (userType === "new") {
      navigate("/payments/subscriptions/subscription-plan");
    } else {
      // Existing users: navigate to subscriptions page (modal auto-opens for existing users)
      navigate("/payments/subscriptions");
    }
  };

  const getSubText = () => {
    if (userType === "existing") {
      return isUrgent
        ? "Decide on a subscription plan now to avoid losing access when your trial ends."
        : `Start your ${planName} subscription early or wait until trial ends`;
    } else {
      return isUrgent
        ? `Your ${planName} plan (${planPrice}) will start automatically on ${billingStartDate}.`
        : "Billing starts automatically after trial ends.";
    }
  };

  return (
    <div
      className={clsx(
        "relative top-0 z-40 px-2.5 py-1 transition-all duration-200 lg:px-5 lg:py-2.5",
        showUrgentStyling ? "bg-danger-light text-neutral-black" : "bg-info text-white",
        className
      )}
      {...props}
    >
      <div
        className={clsx(
          "mx-auto flex max-w-screen-2xl flex-row items-center justify-between gap-4 transition-all duration-200",
          sidebarOpen ? "lg:pl-[250px]" : "lg:pl-[65px]"
        )}
      >
        <div className="flex flex-row items-center gap-x-2">
          <Icon
            name={showUrgentStyling ? "Timer1" : "iconsax-sparkle"}
            variant={showUrgentStyling ? "Outline" : "Bold"}
            size={showUrgentStyling ? 20 : "md"}
            className={clsx("flex-shrink-0", showUrgentStyling && "text-danger")}
            isCustom={!showUrgentStyling}
          />
          <div className="flex flex-col">
            <Typography variant="action" className={clsx(showUrgentStyling && "text-danger")}>
              {isUrgent ? (
                <>
                  Trial ending soon - {daysLeft} {daysLeft === 1 ? "day" : "days"} left!
                </>
              ) : (
                <>
                  {daysLeft} days left in your {planName} plan trial
                </>
              )}
            </Typography>
            <Typography variant={showUrgentStyling ? "paragraph" : "small-regular"} className={clsx(showUrgentStyling && "text-danger")}>
              {getSubText()}
            </Typography>
          </div>
        </div>
        <Button
          variant={showUrgentStyling ? "primary" : "secondary"}
          size="sm"
          onClick={handleStartSubscription}
          className={clsx(
            "flex-shrink-0",
            showUrgentStyling ? "bg-danger text-white hover:bg-danger/90" : "bg-white text-info-dark hover:bg-gray-100"
          )}
        >
          {userType === "existing" ? "Manage Subscription" : "Manage Plans"}
        </Button>
      </div>
    </div>
  );
};
